import { useEffect, useId, useState } from 'react';
import { api, type AccountInfo } from '../client/api';
import { cn } from '../utils/cn';
import { useBoOperator } from './useBoOperator';

// =============================================================================
// 알림 센터 ⚙ "메일 알림 설정" (2026-09-22 대교).
//
// 개인 설정 — 받을 자격은 서버가 정하고 여기서는 "메일로도 받을지"만 고른다.
//   · 스케줄 승인 요청 — 결재자(중앙 승인: 조직 관리자·중앙 관리자 / 그룹 승인: 그 그룹 관리자)
//   · 기기 장애 (긴급) — 조직 관리자에게만 가므로 현재 조직 역할이 Owner 인 사람에게만 항목을 보인다.
//   · 고객사 기기 운영 — 운영사 운영자(bo_operators)에게만. 모든 고객사의 긴급·경고·복구 메일(2026-10-06 대교).
// 토글은 기기 전원 관리와 같은 알약 스위치. 저장은 즉시(PATCH /api/account), 실패하면 되돌린다.
// =============================================================================

type PrefKey = 'notifyEmailFleet' | 'notifyEmailApproval' | 'notifyEmailOpsFleet';

export function EmailPrefsPanel(): JSX.Element {
  const id = useId();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  // 현재 조직에서 Owner 인가. adminRole(h9_super/fo_super)은 운영자 명단에 있는 계정을 Owner 여도 h9_super 로 잡아
  // 항목이 숨었다(2026-09-22 대교 지적) — 계정 API 가 돌려주는 현재 조직 역할로 본다.
  const isOwner = account?.orgRole === 'Owner';
  const isOperator = useBoOperator() === 'allowed';
  const [saving, setSaving] = useState<PrefKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api.getAccount().then((next) => { if (!controller.signal.aborted) { setAccount(next); setError(null); } })
      .catch((e: unknown) => { if (!controller.signal.aborted) setError(e instanceof Error ? e.message : '설정을 불러오지 못했습니다.'); });
    return () => controller.abort();
  }, []);

  const items: ReadonlyArray<{ key: PrefKey; label: string }> = [
    { key: 'notifyEmailApproval', label: '스케줄 승인 요청' },
    ...(isOwner ? [{ key: 'notifyEmailFleet' as PrefKey, label: '기기 장애 (긴급)' }] : []),
    ...(isOperator ? [{ key: 'notifyEmailOpsFleet' as PrefKey, label: '고객사 기기 운영 (긴급·경고·복구)' }] : []),
  ];

  const toggle = async (key: PrefKey) => {
    if (!account || saving) return;
    const previous = account;
    const next = account[key] === false; // 기본값 켬 → undefined 는 켬으로 본다
    setAccount({ ...account, [key]: next });
    setSaving(key); setError(null);
    try { setAccount(await api.updateAccount({ [key]: next })); }
    catch (e) { setAccount(previous); setError(e instanceof Error ? e.message : '저장하지 못했습니다.'); }
    finally { setSaving(null); }
  };

  return (
    <div className="px-3 py-3 text-[13px]" role="group" aria-labelledby={`${id}-title`}>
      <p id={`${id}-title`} className="mb-2 font-semibold text-text">메일로도 받기</p>
      {!account && !error && <p role="status" className="text-text-dim">설정을 불러오는 중…</p>}
      {account && account.userId === null && <p className="text-text-dim">로그인한 계정에서만 설정할 수 있습니다.</p>}
      {account && account.userId !== null && (
        <ul className="divide-y divide-line">
          {items.map(({ key, label }) => {
            const enabled = account[key] !== false;
            const busy = saving === key;
            return (
              <li key={key} className="flex items-center justify-between gap-4 py-2.5">
                <span id={`${id}-${key}`} className="text-text">{label}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-labelledby={`${id}-${key}`}
                  disabled={saving !== null}
                  onClick={() => void toggle(key)}
                  className={cn('relative h-5 w-9 flex-none rounded-full transition-colors',
                    enabled ? 'bg-accent' : 'bg-panel-3',
                    busy && 'opacity-60', saving !== null && !busy && 'cursor-not-allowed')}
                >
                  <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
                    enabled ? 'left-[18px]' : 'left-0.5')} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-2 text-[12px] text-text-dim">화면 알림은 그대로 오고, 메일만 켜고 끕니다.</p>
      {error && <p role="alert" className="mt-1 text-[12px] text-red">{error}</p>}
    </div>
  );
}
